import { useCallback, useEffect, useState } from 'react';
import { MoreHorizontal, Trash2, UserPlus, Crown, Shield, Pencil, Eye, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useCopy, fill } from '../../i18n';
import { cn } from '@/lib/utils';
import { avatarColorFor, initialsFor } from '@/lib/avatarPalette';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogTitle } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import ConfirmModal from '../common/ConfirmModal';
import MobileScreen from './MobileScreen';
import { getProjectMembers, inviteMember, removeMember } from '../../services/projectMemberService';
import type { ProjectMemberWithUser, ProjectMemberRole, ProjectWithRole } from '../../types/database';

/**
 * Equipe do projeto no celular: ver, convidar e remover.
 *
 * ------------------------------------------------------------------
 * POR QUE UMA TELA NOVA, E NAO `team` EM `MODULOS_DE_CELULAR`
 * ------------------------------------------------------------------
 * Aquela chave roteia para `TeamAllocationModule`, que e a grade densa de
 * alocacao pessoa por periodo: trabalho de mesa, explicitamente fora do escopo
 * do telefone. E a lista tem um segundo consumidor, o `UIStateContext`, que a
 * usa para decidir em que modulo o app ABRE: por ela, "Equipe" viraria destino
 * de abertura no celular.
 *
 * Vai a ACAO, nao a tela. E a mesma regua ja aplicada na lista de projetos,
 * onde Favoritar, Renomear, Duplicar e Excluir entraram e "mover para
 * organizacao" ficou de fora.
 *
 * Ate 25/08/2026 nao existia caminho nenhum para convidar alguem pelo celular:
 * o unico formulario de e-mail com papel do produto (`ProjectShareModal`) so e
 * montado a partir da barra superior do desktop. Nao era uma tela dificil de
 * achar no telefone, era uma tela que nao existia la.
 */

const PAPEIS: ProjectMemberRole[] = ['editor', 'viewer', 'admin'];

const ICONE_PAPEL = { owner: Crown, admin: Shield, editor: Pencil, viewer: Eye } as const;

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface EquipeScreenProps {
  projeto: ProjectWithRole;
  aoVoltar: () => void;
}

const EquipeScreen = ({ projeto, aoVoltar }: EquipeScreenProps) => {
  const t = useCopy().common.equipeScreen;
  const tShare = useCopy().modals.projectShare;
  const { user } = useAuth();
  const { canAddMemberToProject, limits } = useSubscription();

  const [membros, setMembros] = useState<ProjectMemberWithUser[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [convidando, setConvidando] = useState(false);
  const [email, setEmail] = useState('');
  const [papel, setPapel] = useState<ProjectMemberRole>('editor');
  const [enviando, setEnviando] = useState(false);
  const [menuDe, setMenuDe] = useState<ProjectMemberWithUser | null>(null);
  const [confirmando, setConfirmando] = useState(false);
  const [ocupado, setOcupado] = useState(false);

  const carregar = useCallback(async () => {
    const { data, error } = await getProjectMembers(projeto.id);
    if (error) toast.error(t.erroCarregar);
    setMembros(data || []);
    setCarregando(false);
  }, [projeto.id, t.erroCarregar]);

  useEffect(() => { void carregar(); }, [carregar]);

  /**
   * Quem pode convidar e remover.
   *
   * A RPC `invite_to_project` exige `role IN ('owner','admin')`, entao um editor
   * que tocasse no botao receberia erro cru do banco. A lista de membros e a
   * fonte assim que carrega; `projeto.userRole` cobre o intervalo antes disso,
   * para o botao nao piscar.
   */
  const meuPapel = membros.find((m) => m.user_id === user?.id)?.role ?? projeto.userRole;
  const podeGerir = meuPapel === 'owner' || meuPapel === 'admin';

  const limite = limits.members_per_project;
  const semVaga = !carregando && !canAddMemberToProject(membros.length);

  const enviar = async () => {
    const limpo = email.trim();
    if (!EMAIL.test(limpo)) { toast.error(tShare.toasts.invalidEmail); return; }
    if (!user) return;

    setEnviando(true);
    try {
      const r = await inviteMember(projeto.id, limpo, papel, user.id);
      if (r.error) {
        // O gatilho do banco so dispara na INSERCAO em project_members, que
        // para conta ja existente e agora e para conta nova e no aceite. Sem
        // este ramo, a pessoa veria a string crua do Postgres.
        const msg = String((r.error as { message?: string }).message || '');
        toast.error(msg.includes('MEMBER_LIMIT_REACHED') ? t.semVaga : msg || tShare.toasts.addError);
        return;
      }
      if (r.invited && r.emailSent === false) {
        toast.warning(fill(tShare.toasts.inviteEmailFailed, { email: limpo }));
      } else if (r.invited) {
        toast.success(fill(tShare.toasts.inviteSent, { email: limpo }));
      } else {
        toast.success(tShare.toasts.memberAdded);
      }
      setEmail('');
      setConvidando(false);
      await carregar();
    } finally {
      setEnviando(false);
    }
  };

  const remover = async () => {
    if (!menuDe) return;
    setOcupado(true);
    try {
      const { error } = await removeMember(projeto.id, menuDe.user_id);
      if (error) { toast.error(t.erroRemover); return; }
      setMembros((antes) => antes.filter((m) => m.user_id !== menuDe.user_id));
      toast.success(t.removido);
    } finally {
      setOcupado(false);
      setConfirmando(false);
      setMenuDe(null);
    }
  };

  const apoio = carregando ? undefined
    : membros.length === 1 ? t.apoioUm
      : fill(t.apoio, { count: membros.length });

  /**
   * Item da folha de ações, mesma fábrica de `ProjetosScreen`.
   *
   * Estava inline aqui, e o auditor de design system reprovou com razão: a Lei 5
   * exige que destrutivo em linha de lista não fique a um toque de distância, e
   * a régua reconhece as formas do produto (`acao(`, `DropdownMenuItem`,
   * `ActionMenu`) para saber que a intenção foi cumprida. Escrever o botão à mão
   * satisfazia a lei e escondia isso da régua, que é a pior combinação: correto
   * hoje, invisível amanhã.
   *
   * Alvo de 48px, como toda linha das telas de celular.
   */
  const acao = (
    chave: string,
    Icone: typeof Trash2,
    rotulo: string,
    aoTocar: () => void,
    perigo = false,
  ) => (
    <button
      key={chave}
      type="button"
      data-testid={`equipe-acao-${chave}`}
      onClick={aoTocar}
      className={cn(
        'flex items-center gap-3 px-5 py-3.5 text-left hover:bg-[var(--color-bg-secondary)]',
        perigo && 'text-[var(--color-danger)]'
      )}
    >
      <Icone size={18} className={cn('shrink-0', !perigo && 'text-[var(--color-text-muted)]')} />
      <span className={cn('text-[15px]', !perigo && 'text-[var(--color-text)]')}>{rotulo}</span>
    </button>
  );

  return (
    <MobileScreen titulo={t.titulo} apoio={apoio} aoVoltar={aoVoltar} rotuloVoltar={t.voltar} semPadding>
      <div className="px-4 pb-5">
        {podeGerir && (
          <div className="pb-3">
            {semVaga ? (
              /* O buraco que os caminhos do TeamModule tem: la o convite e
               * criado, a pessoa aceita, e SO ENTAO o banco derruba com
               * MEMBER_LIMIT_REACHED. No plano Free, que sao 3 membros, isso
               * acontece cedo. Aqui a parede aparece antes de gastar o convite. */
              <div className="rounded-[var(--radius-md)] border border-[var(--color-warning-border)] bg-[var(--color-warning-bg)] px-4 py-3">
                <p className="text-[13px] text-[var(--color-warning-text)]">{t.semVaga}</p>
              </div>
            ) : convidando ? (
              <div className="flex flex-col gap-2">
                <Input
                  autoFocus
                  type="email"
                  inputMode="email"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  placeholder={t.emailPlaceholder}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') void enviar(); if (e.key === 'Escape') setConvidando(false); }}
                  className="h-11"
                />
                <div className="flex items-center gap-2">
                  <Select value={papel} onValueChange={(v) => setPapel(v as ProjectMemberRole)}>
                    <SelectTrigger className="h-11 flex-1 border-[var(--color-border)] bg-[var(--color-bg-secondary)] text-[15px]">
                      <SelectValue placeholder={t.papel} />
                    </SelectTrigger>
                    <SelectContent>
                      {PAPEIS.map((p) => (
                        <SelectItem key={p} value={p}>{tShare.roles[p].label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    variant="primary"
                    className="h-11 shrink-0 px-4"
                    disabled={enviando || !email.trim()}
                    /* Mesmo motivo do rodape do Kanban: no dedo o blur dispara
                     * antes do clique e o botao some debaixo do polegar. */
                    onPointerDown={(e) => e.preventDefault()}
                    onClick={() => void enviar()}
                  >
                    {enviando ? <Loader2 size={15} className="animate-spin" /> : t.enviar}
                  </Button>
                </div>
              </div>
            ) : (
              <Button
                variant="primary"
                data-testid="equipe-convidar"
                className="h-11 w-full"
                icon={UserPlus}
                onClick={() => setConvidando(true)}
              >
                {t.convidar}
              </Button>
            )}

            {limite !== -1 && !carregando && (
              <p className="pt-2 text-[11px] text-[var(--color-text-muted)]">
                {fill(t.contagem, { count: membros.length, limit: limite })}
              </p>
            )}
          </div>
        )}

        <div className="flex flex-col">
          {membros.map((m) => {
            const nome = m.user?.full_name || m.user?.email || m.user_id;
            const souEu = m.user_id === user?.id;
            const Icone = ICONE_PAPEL[m.role as keyof typeof ICONE_PAPEL] ?? Eye;
            const podeRemover = podeGerir && !souEu && m.role !== 'owner';
            return (
              <div key={m.user_id} data-testid="equipe-membro" className="flex items-center gap-3 py-2.5 min-h-[56px] border-b border-[var(--color-border)]">
                <span
                  className="w-9 h-9 shrink-0 grid place-items-center rounded-full text-[13px] font-medium text-white"
                  style={{ backgroundColor: avatarColorFor(m.user_id) }}
                >
                  {initialsFor(nome)}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-[15px] text-[var(--color-text)] truncate">
                    {nome}{souEu && <span className="text-[var(--color-text-muted)]"> ({t.voce})</span>}
                  </p>
                  {m.user?.email && (
                    <p className="text-[11px] text-[var(--color-text-muted)] truncate">{m.user.email}</p>
                  )}
                </div>
                <span className="flex items-center gap-1 shrink-0 text-[13px] text-[var(--color-text-muted)]">
                  <Icone size={13} />
                  {tShare.roles[m.role as keyof typeof tShare.roles]?.label ?? m.role}
                </span>
                {podeRemover && (
                  /* Destrutivo no dedo NAO fica a um toque na linha (Lei 5):
                   * vai para o "..." e ainda passa por confirmacao. */
                  <button
                    type="button"
                    data-testid="equipe-membro-menu"
                    aria-label={t.acoes}
                    onClick={() => setMenuDe(m)}
                    className="w-11 h-11 shrink-0 grid place-items-center rounded-[var(--radius-md)] text-[var(--color-text-muted)]"
                  >
                    <MoreHorizontal size={18} />
                  </button>
                )}
              </div>
            );
          })}

          {/* Só quando a lista está REALMENTE vazia, e não quando você é o
              único membro.

              Era `<= 1`, e com você sozinho no projeto a tela dizia a mesma
              coisa três vezes: a linha com o seu nome, o subtítulo "1 pessoa
              neste projeto" e este parágrafo. Pior, a Lei 4 proíbe exatamente
              isso: seção vazia é ação, não parágrafo, e a ação já está ali
              como "Convidar pessoa".

              Zero membros não deveria acontecer (quem abre o projeto é membro
              dele), mas acontece se a RLS barrar a leitura para um papel. Aí um
              texto é melhor do que uma tela muda. */}
          {!carregando && membros.length === 0 && (
            <p className="py-6 text-[13px] text-[var(--color-text-muted)]">{t.vazio}</p>
          )}
        </div>
      </div>

      <Dialog open={!!menuDe} onOpenChange={(aberto) => { if (!aberto) { setMenuDe(null); setConfirmando(false); } }}>
        <DialogContent className="p-0 gap-0 bg-[var(--color-bg)]">
          <DialogTitle className="px-5 pt-5 pb-3 text-[15px] truncate">
            {menuDe?.user?.full_name || menuDe?.user?.email}
          </DialogTitle>
          <div className="flex flex-col pb-2">
            {acao('remover', Trash2, t.remover, () => setConfirmando(true), true)}
          </div>
        </DialogContent>
      </Dialog>

      {confirmando && menuDe && (
        <ConfirmModal
          isOpen
          title={t.removerTitulo}
          message={fill(t.removerMensagem, { nome: menuDe.user?.full_name || menuDe.user?.email || '' })}
          confirmLabel={t.remover}
          variant="danger"
          isLoading={ocupado}
          onConfirm={remover}
          /* Cancelar fecha TUDO, pelo mesmo motivo da lista de projetos: quem
           * desiste de uma acao sem desfazer volta para a lista, nao para o
           * menu que a ofereceu. */
          onCancel={() => { setConfirmando(false); setMenuDe(null); }}
        />
      )}
    </MobileScreen>
  );
};

export default EquipeScreen;
